import { useEffect, useMemo, useState } from 'react'
import { getByokProviders, type ByokProvidersAccess } from '@/lib/api'

export interface ByokProvidersState {
  providers: ByokProvidersAccess | null
  /** At least one provider may render for this org. Gates the Providers tab/page. */
  anyVisible: boolean
  isLoading: boolean
}

interface FetchResult {
  orgId: string
  providers: ByokProvidersAccess | null
}

/**
 * Which BYOK providers this org may see. Backed by the one-grant rule on the
 * backend (byokProviderAccess): a provider is visible only when the org holds
 * its explicit grant or still has BYOK state for it to clean up. Fails closed:
 * nothing is visible until the fetch for the current org has settled.
 */
export function useByokProviders(orgId: string | null): ByokProvidersState {
  const [result, setResult] = useState<FetchResult | null>(null)

  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    getByokProviders(orgId)
      .then(({ providers }) => {
        if (!cancelled) setResult({ orgId, providers })
      })
      .catch(() => {
        // Transient failure: settle this org with its own last known providers.
        // Never reuse another org's map; null (first load or after an org
        // switch) keeps everything hidden, which is the fail-closed default.
        if (!cancelled) setResult(prev => ({ orgId, providers: prev?.orgId === orgId ? prev.providers : null }))
      })
    return () => {
      cancelled = true
    }
  }, [orgId])

  return useMemo(() => {
    if (!orgId) return { providers: null, anyVisible: false, isLoading: false }
    if (!result || result.orgId !== orgId) return { providers: null, anyVisible: false, isLoading: true }
    const providers = result.providers
    return {
      providers,
      anyVisible: !!providers && Object.values(providers).some(provider => provider.visible),
      isLoading: false,
    }
  }, [orgId, result])
}
